import { useState } from "react";
import Profile from "./Profile";
import { FiArrowLeft, FiCheckCircle, FiClock, FiRepeat, FiGlobe, FiSend, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { submitPackageRequest } from "../Api/packageRequestApi";

const Pricing = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("basic");
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    city: "",
    description: "",
  });

  const packageDetails = {
    basic: {
      name: "BASIC LANDING PAGE",
      description: "Perfect for promotions, ads, startups, and single-offer businesses",
      price: 3999,
      pages: "Landing Page",
      features: [
        "Single Landing Page",
        "Mobile & Desktop Responsive",
        "Custom UI Design",
        "SEO Friendly Structure",
        "Contact Form Integration",
        "1 Year Free Hosting",
        "5–7 Days Delivery",
        "1 Free Revision Round"
      ],
      highlights: ["Perfect for startups", "Quick turnaround", "Lead generation focused"],
      color: "from-blue-500 to-cyan-500"
    },
    standard: {
      name: "STANDARD WEBSITE",
      description: "Ideal for small businesses that need a professional online presence",
      price: 9999,
      pages: "4 Pages",
      features: [
        "4 Pages Website",
        "Mobile & Desktop Responsive",
        "Custom UI Design",
        "SEO Friendly Structure",
        "Contact Form Integration",
        "1 Year Free Hosting",
        "5–7 Days Delivery",
        "2 Revision Rounds",
        "Basic Analytics Setup",
        "Blog/News Section"
      ],
      highlights: ["Complete solution", "Professional presence", "Growth potential"],
      color: "from-purple-500 to-pink-500"
    },
    premium: {
      name: "PREMIUM WEBSITE",
      description: "Best for businesses needing full control with admin panel access",
      price: 14999,
      pages: "5 Pages",
      features: [
        "5 Pages Website",
        "Mobile & Desktop Responsive",
        "Custom UI Design",
        "SEO Friendly Structure",
        "Contact Form Integration",
        "Admin Panel Included",
        "1 Year Free Hosting",
        "5–7 Days Delivery",
        "Unlimited Revisions",
        "Advanced Analytics",
        "Blog Management System",
        "E-commerce Ready"
      ],
      highlights: ["Full control", "Scalable architecture", "Future-proof"],
      color: "from-orange-500 to-red-500"
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.mobile) {
      alert("Name and Mobile are required");
      return;
    }

    const payload = {
      package: activeTab,
      price: packageDetails[activeTab].price,
      pages: packageDetails[activeTab].pages,
      ...formData,
    };

    try {
      setLoading(true);
      await submitPackageRequest(payload);
      setShowForm(false);
      setShowThankYou(true);

      setTimeout(() => {
        setShowThankYou(false);
        window.open(
          `https://wa.me/918940281615?text=Hi, my name is ${formData.name}. I am interested in the ${activeTab.toUpperCase()} package.`,
          "_blank"
        );
      }, 3000);
    } catch (error) {
      console.error(error);
      alert("Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const current = packageDetails[activeTab];

  return (
    <div className="bg-gradient-to-br from-[#0a0a0a] via-[#121212] to-[#1a1a1a] text-white min-h-screen px-4 md:px-12 py-8">
      
      {/* Animated background */}
      <div className="fixed inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#914EEE] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
      </div>

      <div className="flex flex-col md:flex-row gap-8 relative z-10">
        
        {/* LEFT – Profile */}
        <div className="md:w-[35%] md:mt-10">
          <Profile />
        </div>

        {/* RIGHT – Pricing */}
        <div className="md:w-[65%]">

          {/* Back Button */}
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-gray-400 hover:text-[#914EEE] mb-8 text-sm transition-colors group"
          >
            <FiArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </button>

          {/* Header */}
          <div className="mb-12 space-y-4">
            <div className="inline-block">
              <span className="text-xs md:text-sm font-semibold text-[#914EEE] bg-[#914EEE]/10 px-4 py-2 rounded-full">
                💰 TRANSPARENT PRICING
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              Simple, Honest,
              <br />
              <span className="text-[#914EEE]">No Hidden Costs</span>
            </h1>
            <p className="text-gray-400 text-base max-w-2xl">
              Choose the perfect package for your business. All plans include free hosting, 
              responsive design, and professional support.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 md:gap-4 mb-12 border-b border-gray-700 pb-6 overflow-x-auto">
            {["basic", "standard", "premium"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`relative px-6 py-3 font-bold uppercase tracking-wider text-sm transition-all duration-300 whitespace-nowrap ${
                  activeTab === tab
                    ? "text-[#914EEE]"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                {tab}
                {activeTab === tab && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#914EEE] to-purple-600 rounded-t"></div>
                )}
              </button>
            ))}
          </div>

          {/* Pricing Card */}
          <div className={`bg-gradient-to-br ${current.color} p-[1px] rounded-2xl mb-12`}>
            <div className="bg-[#141414] border border-gray-700 p-8 md:p-10 rounded-2xl space-y-8">

              {/* Header */}
              <div className="space-y-4">
                <h2 className="text-3xl md:text-4xl font-bold tracking-wide">
                  {current.name}
                </h2>
                <p className="text-gray-400 text-base max-w-xl">
                  {current.description}
                </p>
                <div className="flex items-end gap-2 pt-4">
                  <span className="text-5xl md:text-6xl font-bold text-[#914EEE]">
                    ₹{current.price.toLocaleString()}
                  </span>
                  <span className="text-gray-400 text-sm mb-2">One-time payment</span>
                </div>
              </div>

              {/* Meta Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-2 text-gray-400">
                  <FiClock className="text-[#914EEE]" />
                  <span className="text-sm">5–7 Days Delivery</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <FiRepeat className="text-[#914EEE]" />
                  <span className="text-sm">Revisions Included</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <FiGlobe className="text-[#914EEE]" />
                  <span className="text-sm">1 Year Free Hosting</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <FiCheckCircle className="text-[#914EEE]" />
                  <span className="text-sm">Free Support</span>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent"></div>

              {/* Highlights */}
              <div className="space-y-3">
                <p className="text-sm font-semibold text-gray-300">Why choose this package?</p>
                <div className="flex flex-wrap gap-2">
                  {current.highlights.map((h, i) => (
                    <span 
                      key={i} 
                      className="text-xs bg-[#914EEE]/20 text-[#914EEE] px-3 py-2 rounded-full font-medium"
                    >
                      {h}
                    </span>
                  ))}
                </div>
              </div>

              {/* Features */}
              <div className="space-y-4">
                <h3 className="font-bold text-lg text-white">What's Included</h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {current.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-gray-300">
                      <FiCheckCircle className="text-[#914EEE] flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA Button */}
              <button
                onClick={() => setShowForm(true)}
                className={`w-full bg-gradient-to-r ${current.color} text-white py-4 font-bold uppercase tracking-widest rounded-lg hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 text-sm md:text-base group`}
              >
                <FiSend className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                Get Started Now
              </button>

              <p className="text-center text-xs text-gray-500">
                Free consultation • Limited slots available • Money-back guarantee
              </p>
            </div>
          </div>

          {/* Comparison CTA */}
          <div className="bg-[#1A1A1A]/80 border border-gray-700 p-8 rounded-xl text-center space-y-4">
            <h3 className="text-xl font-bold">Not Sure Which Package?</h3>
            <p className="text-gray-400">
              Let's discuss your project requirements and find the perfect solution
            </p>
            <button
              onClick={() => navigate("/")}
              className="inline-block text-[#914EEE] hover:underline font-semibold"
            >
              Schedule a Free Consultation →
            </button>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur flex items-center justify-center z-50 px-4">
          <div className="bg-[#141414] border border-gray-700 max-w-md w-full p-8 rounded-xl space-y-6 animate-in">
            
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-bold">
                Get Started
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <FiX size={24} />
              </button>
            </div>

            <p className="text-gray-400">
              {current.name} - ₹{current.price.toLocaleString()}
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-[#1A1A1A] border border-gray-700 px-4 py-3 text-white rounded-lg outline-none focus:border-[#914EEE] transition-colors text-sm placeholder-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  placeholder="+91 9XXXXXXXXX"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  className="w-full bg-[#1A1A1A] border border-gray-700 px-4 py-3 text-white rounded-lg outline-none focus:border-[#914EEE] transition-colors text-sm placeholder-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-[#1A1A1A] border border-gray-700 px-4 py-3 text-white rounded-lg outline-none focus:border-[#914EEE] transition-colors text-sm placeholder-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  City
                </label>
                <input
                  type="text"
                  placeholder="Your city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full bg-[#1A1A1A] border border-gray-700 px-4 py-3 text-white rounded-lg outline-none focus:border-[#914EEE] transition-colors text-sm placeholder-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Project Description
                </label>
                <textarea
                  rows="4"
                  placeholder="Tell me about your project..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-[#1A1A1A] border border-gray-700 px-4 py-3 text-white rounded-lg outline-none focus:border-[#914EEE] transition-colors text-sm placeholder-gray-500"
                ></textarea>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowForm(false)}
                className="flex-1 border border-gray-700 py-3 text-sm font-semibold rounded-lg hover:bg-[#1A1A1A] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-[#914EEE] to-purple-600 py-3 text-sm font-semibold rounded-lg hover:shadow-lg hover:shadow-[#914EEE]/50 transition-all disabled:opacity-50"
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Thank You Modal */}
      {showThankYou && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur flex items-center justify-center z-50 px-4">
          <div className="bg-[#141414] border border-[#914EEE]/50 max-w-sm w-full p-8 rounded-xl text-center space-y-6 animate-in">
            <div className="text-6xl">🎉</div>
            <h3 className="text-2xl font-bold">Thank You!</h3>
            <p className="text-gray-400">
              Your request has been received. Our team will contact you shortly.
            </p>
            <p className="text-[#914EEE] text-sm font-semibold">
              Redirecting to WhatsApp...
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;